import { ReactNode } from 'react';
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { twMerge } from 'tailwind-merge';
import { FormField } from './FormField';

export interface FormRadioCardOption {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
}

interface FormRadioCardsProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  options: FormRadioCardOption[];
  rootClassName?: string;
  itemClassName?: string;
}

export const FormRadioCards = <T extends FieldValues>({
  name,
  control,
  label,
  options,
  rootClassName,
  itemClassName,
}: FormRadioCardsProps<T>) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  return (
    <FormField label={label} error={error?.message}>
      <RadioGroup.Root
        value={value as string}
        onValueChange={onChange}
        variant="card"
        className={twMerge('flex', rootClassName)}
      >
        {options.map(option => (
          <RadioGroup.Item
            key={option.value}
            value={option.value}
            className={twMerge('flex items-center gap-2', itemClassName)}
          >
            <RadioGroup.IndicatorContainer>
              <RadioGroup.Indicator />
            </RadioGroup.IndicatorContainer>
            <div className="flex flex-col gap-0.5">
              {option.icon && <div className="text-blue-600">{option.icon}</div>}
              <div className="text-sm font-semibold text-gray-900">{option.label}</div>
              {option.description && (
                <div className="text-xs text-gray-500">{option.description}</div>
              )}
            </div>
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
    </FormField>
  );
};
